/**
 * Safe reader for the API's `APIResponse` envelope.
 *
 * Why this exists: the thunks used to call `await response.json()` before
 * checking `response.ok`. ASP.NET Core's JWT middleware rejects an expired or
 * missing token with a bare 401 and **no response body**, so `.json()` threw
 * `Unexpected end of JSON input`. That parse error then surfaced to the user
 * instead of the real problem, and because the rejection message wasn't
 * `SESSION_EXPIRED` the session middleware never logged them out - the admin
 * panel just sat there showing a confusing error.
 *
 * This reads the body as text first and only interprets an *empty* 401 as an
 * expired session. Endpoints that fail with 401 *and* a payload (a failed login
 * returns "Invalid email or password") keep their real message.
 *
 * Returns `any` on purpose so existing call-site annotations
 * (`const data: ApiResponse = await readApiResponse(res)`) keep compiling.
 */
/**
 * The message used whenever a request fails because the session is gone.
 *
 * It doubles as the signal `sessionMiddleware` matches on, which is why it's a
 * single shared constant: it's user-facing text (so a component that blindly
 * toasts the rejection reason shows something sensible) *and* machine-readable.
 */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

/** Legacy sentinels some slices still reject with directly. */
const SESSION_EXPIRED_SENTINELS = ['SESSION_EXPIRED', 'Unauthorized', SESSION_EXPIRED_MESSAGE];

/** True when a thunk rejection means "the session is over", not "this action failed". */
export function isSessionExpiredError(payload: unknown): boolean {
  return typeof payload === 'string' && SESSION_EXPIRED_SENTINELS.includes(payload);
}

export async function readApiResponse(response: Response): Promise<any> {
  let text = '';
  try {
    text = await response.text();
  } catch {
    // Body already consumed or the stream failed - fall through to the
    // status-based envelope below.
  }

  if (text) {
    try {
      return JSON.parse(text);
    } catch {
      // HTML error page, proxy response, or truncated payload.
      return {
        isSuccess: false,
        result: null,
        statusCode: response.status,
        errorMessages: [
          response.ok
            ? 'Unexpected response from the server.'
            : `Request failed with status ${response.status}.`,
        ],
      };
    }
  }

  // Empty body from here on.
  if (response.status === 401) {
    // sessionMiddleware matches this message and drives the logout + redirect,
    // and it reads correctly if a component toasts the rejection reason as-is.
    return {
      isSuccess: false,
      result: null,
      statusCode: 401,
      errorMessages: [SESSION_EXPIRED_MESSAGE],
    };
  }

  if (response.status === 403) {
    // Signed in but lacking the role. Not a session problem, so don't log out.
    return {
      isSuccess: false,
      result: null,
      statusCode: 403,
      errorMessages: ['You do not have permission to perform this action.'],
    };
  }

  return {
    isSuccess: response.ok,
    result: null,
    statusCode: response.status,
    errorMessages: response.ok ? [] : [`Request failed with status ${response.status}.`],
  };
}
